import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add auth token and restaurant ID to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add X-Restaurant-Id header from persisted Zustand state
  try {
    const authState = localStorage.getItem('pleko-auth');
    if (authState) {
      const parsed = JSON.parse(authState);
      const restaurantId = parsed.state?.currentRestaurantId;
      if (restaurantId) {
        config.headers['X-Restaurant-Id'] = restaurantId.toString();
      }
    }
  } catch {
    // Ignore parsing errors
  }

  return config;
});

// Handle 401 (unauthorized) and 402 (payment required) responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    // 402 Payment Required — DOS significados distintos, y el cuerpo es lo que
    // los separa. Antes esto redirigia CUALQUIER 402 a /trial-expired y, peor,
    // el "swallow" de la promesa que nunca resuelve estaba FUERA del `if`,
    // asi que se comia el error siempre: el llamante nunca entraba en su `catch` y la pagina se
    // quedaba girando. Anadir rutas a una lista blanca no arreglaba eso.
    //
    // Discriminamos por el campo `error` del cuerpo, no por la ruta del
    // navegador:
    //   `trial_ended`   (CheckTrialStatus) -> la prueba ha caducado y la app
    //                   entera esta en solo lectura: redirigir es correcto.
    //   cualquier otro  (`plan_required` de EnsurePlanFeature, `no_plan` de las
    //                   cuotas) -> es un limite de UNA feature: el error tiene
    //                   que llegar al llamante para que la pantalla pinte su
    //                   propio estado. Redirigir aqui le ensenaria al usuario un
    //                   "tu prueba ha caducado" que es falso.
    //
    // Sin lista blanca de rutas: la discriminacion por cuerpo la hace
    // innecesaria y sirve igual a `lab` y `time_tracking` cuando les toque su
    // puerta (ver 10-CONTEXT.md <deferred>).
    if (error.response?.status === 402) {
      if (error.response?.data?.error === 'trial_ended') {
        const currentPath = window.location.pathname;
        const allowedPaths = ['/trial-expired', '/upgrade', '/settings', '/subscription'];
        if (!allowedPaths.some(path => currentPath.startsWith(path))) {
          window.location.href = '/trial-expired';
        }
        // Solo se traga el error cuando efectivamente redirige: sin esto, la
        // peticion en vuelo dejaria un rechazo sin manejar durante la navegacion.
        return new Promise(() => {});
      }
      // No es trial: que lo maneje quien llamo.
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  email_verified_at: string | null;
  trial_ends_at: string;
  trial_days_remaining: number;
  on_trial: boolean;
  trial_ended: boolean;
  is_admin?: boolean;
  partner?: {
    name: string;
    code: string;
    discount_percentage: number;
    allowed_plans: string[] | null;
  } | null;
  setup_fee_status?: 'pending' | 'paid' | 'waived';
  max_restaurants?: number;
  can_multi_restaurant?: boolean;
}

export interface Restaurant {
  id: number;
  name: string;
  city: string | null;
  role?: RestaurantRole;
}

// Restaurant Settings (extended)
export interface RestaurantSettings {
  id: number;
  name: string;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  phone: string | null;
  email: string | null;
  tax_id: string | null;
  default_food_cost_target: number;
  currency: string;
  timezone: string;
  ingredient_prices_include_tax: boolean;
  sales_tax_rate: number;
  shifts: { name: string; key: string; start: string; end: string }[] | null;
  created_at: string;
  updated_at: string;
}

export interface Currency {
  code: string;
  name: string;
  symbol: string;
}

export interface Timezone {
  id: string;
  name: string;
}

// Team Types
export type RestaurantRole = 'owner' | 'admin' | 'chef' | 'viewer' | 'time_only';

export interface RestaurantMember {
  id: number;
  user_id: number;
  restaurant_id: number;
  role: RestaurantRole;
  joined_at: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
}

export interface RestaurantInvitation {
  id: number;
  restaurant_id: number;
  email: string;
  role: RestaurantRole;
  role_name: string;
  expires_at: string;
  created_at: string;
  inviter: {
    id: number;
    name: string;
  };
}

export interface TeamInfo {
  members: RestaurantMember[];
  pending_invitations: RestaurantInvitation[];
  max_users: number;
  remaining_slots: number;
}

export interface PendingInvitation {
  id: number;
  email: string;
  role: RestaurantRole;
  role_name: string;
  expires_at: string;
  created_at: string;
  restaurant: {
    id: number;
    name: string;
    city: string | null;
  };
  inviter: {
    name: string;
  };
}

export interface IngredientCategory {
  id: number;
  restaurant_id: number;
  name: string;
  parent_id: number | null;
  icon: string | null;
  color: string | null;
  sort_order: number;
  tax_rate: number;
  ingredients_count?: number;
  children?: IngredientCategory[];
}

export interface RecipeCategory {
  id: number;
  restaurant_id: number;
  name: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  recipes_count?: number;
}

export interface Supplier {
  id: number;
  restaurant_id: number;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  tax_id: string | null;
  rgseaa_number: string | null;
  certifications: string[] | null;
  approval_status: 'approved' | 'conditional' | 'pending_review' | 'blocked';
  last_audit_date: string | null;
  next_audit_date: string | null;
  quality_rating: number | null;
  notes: string | null;
  is_active: boolean;
  ingredients_count?: number;
}

export interface IngredientSupplier {
  id: number;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  price: number;
  sku: string | null;
  is_preferred: boolean;
  last_purchase_date: string | null;
  min_order_quantity: number | null;
  delivery_days: number | null;
  notes: string | null;
}

export interface SupplierComparison {
  ingredient: Ingredient;
  suppliers: Array<{
    id: number;
    name: string;
    price: number;
    is_preferred: boolean;
    last_purchase_date: string | null;
    delivery_days: number | null;
  }>;
  cheapest: {
    id: number;
    name: string;
    price: number;
  } | null;
  preferred: {
    id: number;
    name: string;
    price: number;
  } | null;
  potential_savings: number;
  potential_savings_percent: number;
}

export interface IngredientNutrition {
  calories: number | null;      // kcal/100g
  protein: number | null;       // g/100g
  carbs: number | null;         // g/100g
  fat: number | null;           // g/100g
  fiber: number | null;         // g/100g
  sugar: number | null;         // g/100g
  saturated_fat: number | null; // g/100g
  sodium: number | null;        // mg/100g
}

export interface Ingredient {
  id: number;
  restaurant_id: number;
  name: string;
  unit: string;
  current_price: number;
  purchase_format: string | null;
  units_per_format: number | null;
  purchase_price: number | null;
  category_id: number | null;
  default_supplier_id: number | null;
  waste_percentage: number;
  min_stock: number | null;
  current_stock: number | null;
  sku: string | null;
  barcode: string | null;
  notes: string | null;
  allergens: string[] | null;
  is_active: boolean;
  last_purchase_date: string | null;
  // Nutrition (per 100g)
  nutrition_calories: number | null;
  nutrition_protein: number | null;
  nutrition_carbs: number | null;
  nutrition_fat: number | null;
  nutrition_fiber: number | null;
  nutrition_sugar: number | null;
  nutrition_saturated_fat: number | null;
  nutrition_sodium: number | null;
  nutrition_source: 'manual' | 'openfoodfacts' | null;
  despiece_output_id?: number | null;
  is_despiece_output?: boolean;
  storage_location_id?: number | null;
  storage_location?: StorageLocation | null;
  category?: IngredientCategory;
  default_supplier?: Supplier;
  // Present when listed from a supplier (GET /suppliers/{id}/ingredients)
  pivot?: SupplierIngredientLink;
  is_default_supplier?: boolean;
}

/** How one ingredient is linked to one supplier. */
export interface SupplierIngredientLink {
  ingredient_id: number;
  supplier_id: number;
  price: number;
  sku: string | null;
  is_preferred: boolean;
  last_purchase_date: string | null;
  min_order_quantity: number | null;
  delivery_days: number | null;
  notes: string | null;
}

export interface StorageLocation {
  id: number;
  restaurant_id: number;
  name: string;
  type: 'cold' | 'frozen' | 'ambient';
  temperature_zone: string | null;
  min_temperature: number | null;
  max_temperature: number | null;
  sort_order: number;
  ingredients_count?: number;
}

export interface IngredientPriceHistory {
  id: number;
  ingredient_id: number;
  supplier_id: number | null;
  price: number;
  date: string;
  source: 'manual' | 'invoice' | 'ocr' | 'import';
  reference: string | null;
  notes: string | null;
  supplier?: Supplier;
}

export interface RecipeIngredient {
  id: number;
  recipe_id: number;
  ingredient_id: number | null;
  sub_recipe_id: number | null;
  supplier_id: number | null;
  quantity: number;
  unit: string;
  waste_percentage: number;
  sort_order: number;
  notes: string | null;
  ingredient?: Ingredient;
  supplier?: Supplier;
  sub_recipe?: Recipe;
  is_sub_recipe: boolean;
  // Computed fields
  gross_cost?: number;
  cost?: number;
  cost_percentage?: number;
}

export interface RecipeStep {
  id: number;
  recipe_id: number;
  step_number: number;
  description: string;
  duration: number | null;
  image_url: string | null;
  tips: string | null;
}

export interface SubRecipeOption {
  id: number;
  name: string;
  yield_quantity: number | null;
  yield_unit: string | null;
  total_cost: number;
  cost_per_yield_unit: number;
  status: string;
  category_id: number | null;
  category_name: string | null;
  category_icon: string | null;
  has_yield: boolean;
}

export interface Recipe {
  id: number;
  restaurant_id: number;
  name: string;
  description: string | null;
  portions: number;
  portion_weight: number | null;
  yield_quantity: number | null;
  yield_unit: string | null;
  pvp: number | null;
  prep_time: number | null;
  cook_time: number | null;
  difficulty: 'easy' | 'medium' | 'hard';
  category_id: number | null;
  image_url: string | null;
  status: 'draft' | 'active' | 'archived';
  tags: string[] | null;
  notes: string | null;
  is_active: boolean;
  category?: RecipeCategory;
  recipe_ingredients?: RecipeIngredient[];
  steps?: RecipeStep[];
  // Computed fields
  total_cost?: number;
  cost_per_portion?: number;
  food_cost_percentage?: number | null;
  food_cost_status?: 'green' | 'yellow' | 'red' | 'unknown';
  total_time?: number | null;
  profit_margin?: number | null;
  allergens?: string[];
  allergen_labels?: AllergenInfo[];
  nutri_score?: 'A' | 'B' | 'C' | 'D' | 'E' | null;
}

export interface EscandalloTotals {
  ingredients_count: number;
  total_gross_cost: number;
  total_waste_cost: number;
  total_cost: number;
  total_cost_net: number;
  cost_per_portion: number;
  cost_per_portion_net: number;
  pvp: number | null;
  pvp_net: number | null;
  sales_tax_rate: number;
  prices_include_tax: boolean;
  food_cost_percentage: number | null;
  food_cost_status: 'green' | 'yellow' | 'red' | 'unknown';
  profit_margin: number | null;
  profit_margin_percentage: number | null;
  total_weight_grams: number | null;
  weight_per_portion: number | null;
  cost_per_kg: number | null;
}

export interface EscandalloAnalysis {
  is_profitable: boolean;
  needs_attention: boolean;
  is_critical: boolean;
  recommendations: Array<{
    type: 'critical' | 'warning' | 'info' | 'suggestion';
    message: string;
    suggested_pvp?: number;
  }>;
  top_cost_ingredients: Array<{
    id: number;
    name: string;
    net_cost: number;
    cost_percentage: number;
  }>;
  high_waste_ingredients: Array<{
    id: number;
    name: string;
    waste_percentage: number;
  }>;
}

export interface Escandallo {
  recipe: {
    id: number;
    name: string;
    portions: number;
    pvp: number | null;
    prep_time: number | null;
    cook_time: number | null;
    total_time: number | null;
  };
  ingredients: Array<{
    id: number;
    name: string;
    quantity: number;
    unit: string;
    unit_price: number;
    ingredient_unit: string;
    waste_percentage: number;
    gross_cost: number;
    waste_cost: number;
    net_cost: number;
    cost_percentage: number | null;
  }>;
  totals: EscandalloTotals;
  analysis: EscandalloAnalysis;
}

export interface PaginatedResponse<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface LoginResponse {
  message: string;
  user: User;
  token: string;
}

export interface RegisterResponse {
  message: string;
  user: User;
  restaurant: Restaurant;
  token: string;
}

export interface UserResponse {
  user: User;
  restaurants: Restaurant[];
}

// Auth API
export const authApi = {
  register: (data: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone?: string;
    restaurant_name?: string;
    partner_code?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    referrer?: string;
  }) => api.post<RegisterResponse>('/register', data),

  login: (data: { email: string; password: string }) =>
    api.post<LoginResponse>('/login', data),

  logout: () => api.post('/logout'),

  getUser: () => api.get<UserResponse>('/user'),

  forgotPassword: (data: { email: string }) =>
    api.post<{ message: string }>('/forgot-password', data),

  resetPassword: (data: {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
  }) => api.post<{ message: string }>('/reset-password', data),
};

// Leads API (partial signups)
export const leadsApi = {
  capture: (data: {
    email: string;
    source_page?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    referrer?: string;
  }) => api.post('/leads', data),
};

// Ingredients API
export const ingredientsApi = {
  list: (params: {
    restaurant_id: number;
    category_id?: number;
    supplier_id?: number;
    search?: string;
    is_active?: boolean;
    low_stock?: boolean;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<Ingredient>>('/ingredients', { params }),

  get: (id: number) => api.get<{
    ingredient: Ingredient;
    price_change_percentage: number | null;
    effective_price: number;
    is_low_stock: boolean;
  }>(`/ingredients/${id}`),

  create: (data: Partial<Ingredient>) => api.post<{
    message: string;
    ingredient: Ingredient;
  }>('/ingredients', data),

  update: (id: number, data: Partial<Ingredient>) => api.put<{
    message: string;
    ingredient: Ingredient;
  }>(`/ingredients/${id}`, data),

  delete: (id: number) => api.delete<{ message: string }>(`/ingredients/${id}`),

  priceHistory: (id: number, params?: {
    start_date?: string;
    end_date?: string;
    per_page?: number;
  }) => api.get<PaginatedResponse<IngredientPriceHistory>>(`/ingredients/${id}/price-history`, { params }),

  updatePrice: (id: number, data: {
    price: number;
    supplier_id?: number;
    date?: string;
    source?: string;
    reference?: string;
    notes?: string;
  }) => api.post<{
    message: string;
    ingredient: Ingredient;
  }>(`/ingredients/${id}/update-price`, data),

  suggestAllergens: (name: string) => api.post<{
    suggestions: string[];
    labels: AllergenInfo[];
  }>('/ingredients/suggest-allergens', { name }),

  getAllergens: () => api.get<{
    allergens: Record<string, AllergenInfo>;
  }>('/allergens'),

  // Ingredient Suppliers
  listSuppliers: (ingredientId: number) => api.get<{
    ingredient: Ingredient;
    suppliers: IngredientSupplier[];
  }>(`/ingredients/${ingredientId}/suppliers`),

  addSupplier: (ingredientId: number, data: {
    supplier_id: number;
    price: number;
    sku?: string;
    is_preferred?: boolean;
    last_purchase_date?: string;
    min_order_quantity?: number;
    delivery_days?: number;
    notes?: string;
  }) => api.post<{
    message: string;
    supplier: Supplier;
  }>(`/ingredients/${ingredientId}/suppliers`, data),

  updateSupplier: (ingredientId: number, supplierId: number, data: {
    price?: number;
    sku?: string;
    is_preferred?: boolean;
    last_purchase_date?: string;
    min_order_quantity?: number;
    delivery_days?: number;
    notes?: string;
  }) => api.put<{
    message: string;
  }>(`/ingredients/${ingredientId}/suppliers/${supplierId}`, data),

  removeSupplier: (ingredientId: number, supplierId: number) =>
    api.delete<{ message: string }>(`/ingredients/${ingredientId}/suppliers/${supplierId}`),

  setPreferredSupplier: (ingredientId: number, supplierId: number) =>
    api.put<{ message: string }>(`/ingredients/${ingredientId}/suppliers/${supplierId}/set-preferred`),

  compareSuppliers: (ingredientId: number) =>
    api.get<SupplierComparison>(`/ingredients/${ingredientId}/suppliers/comparison`),
};

// Excel Import Types
export interface ImportField {
  key: string;
  label: string;
  required: boolean;
}

export interface ImportUploadResponse {
  session_id: string;
  headers: string[];
  total_rows: number;
  suggested_mappings: Record<number, string>;
  available_fields: ImportField[];
  preview: Array<Record<string, unknown>>;
}

export interface ImportValidationResponse {
  valid_count: number;
  error_count: number;
  warning_count: number;
  errors: Array<{
    row: number;
    data: Record<string, unknown>;
    messages: string[];
  }>;
  warnings: Array<{
    row: number;
    field: string;
    message: string;
  }>;
  new_categories: string[];
  new_suppliers: string[];
  preview: Array<{
    row: number;
    data: Record<string, unknown>;
  }>;
}

export interface ImportStats {
  created: number;
  updated: number;
  skipped: number;
  categories_created: number;
}

// Excel Import API
export const excelImportApi = {
  downloadTemplate: () =>
    api.get('/ingredients/import/template', { responseType: 'blob' }),

  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ImportUploadResponse>('/ingredients/import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  validate: (sessionId: string, mappings: Record<number, string>) =>
    api.post<ImportValidationResponse>('/ingredients/import/validate', {
      session_id: sessionId,
      mappings,
    }),

  execute: (sessionId: string, options: {
    create_categories?: boolean;
    update_existing?: boolean;
  }) => api.post<{ message: string; stats: ImportStats }>('/ingredients/import/execute', {
    session_id: sessionId,
    ...options,
  }),

  cancel: (sessionId: string) =>
    api.post<{ message: string }>('/ingredients/import/cancel', { session_id: sessionId }),
};

// Sales Import API
export interface SalesImportUploadResponse {
  session_id: string;
  filename: string;
  headers: string[];
  total_rows: number;
  suggested_mappings: Record<number, string>;
  preview: Record<string, unknown>[];
  duplicate_warning: {
    message: string;
    batch_id: number;
    stats: Record<string, number>;
  } | null;
  available_fields: { key: string; label: string; required: boolean }[];
}

export interface SalesImportValidationResponse {
  orders: number;
  items: number;
  total_amount: number;
  date_from: string | null;
  date_to: string | null;
  errors: { row: number; message: string }[];
  warnings: { row: number; message: string }[];
  overlap: {
    has_overlap: boolean;
    existing_orders: number;
    existing_items: number;
    existing_total: number;
    date_from: string;
    date_to: string;
  } | null;
  diff: {
    identical: number;
    new: number;
    total: number;
  };
  preview: Record<string, unknown>[];
}

export interface SalesImportBatch {
  id: number;
  restaurant_id: number;
  filename: string;
  file_hash: string;
  imported_by: number;
  date_from: string;
  date_to: string;
  stats: {
    orders_created: number;
    items_created: number;
    items_skipped: number;
    items_matched: number;
    items_unmatched: number;
    total_amount: number;
  } | null;
  status: 'completed' | 'rolled_back';
  rolled_back_at: string | null;
  rolled_back_by: number | null;
  created_at: string;
  imported_by_user?: { id: number; name: string };
  rolled_back_by_user?: { id: number; name: string } | null;
}

export const salesImportApi = {
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<SalesImportUploadResponse>('/sales/import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  validate: (sessionId: string, mappings: Record<number, string>, fallbackDate?: string) =>
    api.post<SalesImportValidationResponse>('/sales/import/validate', {
      session_id: sessionId,
      mappings,
      fallback_date: fallbackDate,
    }),

  execute: (sessionId: string) =>
    api.post<{ batch_id: number; stats: Record<string, number>; message: string }>(
      '/sales/import/execute',
      { session_id: sessionId }
    ),

  cancel: (sessionId: string) =>
    api.post<{ message: string }>('/sales/import/cancel', { session_id: sessionId }),

  listBatches: () =>
    api.get<{ data: SalesImportBatch[] }>('/sales/imports'),

  rollback: (batchId: number) =>
    api.delete<{ message: string; deleted_stats: Record<string, number> }>(`/sales/imports/${batchId}`),
};

// Ingredient Categories API
export const categoriesApi = {
  list: (params: { restaurant_id: number; flat?: boolean }) =>
    api.get<{ categories: IngredientCategory[] }>('/ingredient-categories', { params }),

  get: (id: number) => api.get<{ category: IngredientCategory }>(`/ingredient-categories/${id}`),

  create: (data: Partial<IngredientCategory>) => api.post<{
    message: string;
    category: IngredientCategory;
  }>('/ingredient-categories', data),

  update: (id: number, data: Partial<IngredientCategory>) => api.put<{
    message: string;
    category: IngredientCategory;
  }>(`/ingredient-categories/${id}`, data),

  delete: (id: number) => api.delete<{ message: string }>(`/ingredient-categories/${id}`),
};

// Suppliers API
export const suppliersApi = {
  list: (params: {
    restaurant_id: number;
    search?: string;
    is_active?: 0 | 1;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<Supplier>>('/suppliers', { params }),

  get: (id: number) => api.get<{ supplier: Supplier }>(`/suppliers/${id}`),

  create: (data: Partial<Supplier>) => api.post<{
    message: string;
    supplier: Supplier;
  }>('/suppliers', data),

  update: (id: number, data: Partial<Supplier>) => api.put<{
    message: string;
    supplier: Supplier;
  }>(`/suppliers/${id}`, data),

  delete: (id: number) => api.delete<{ message: string }>(`/suppliers/${id}`),

  ingredients: (
    id: number,
    params?: { per_page?: number; page?: number; search?: string; is_active?: 0 | 1 },
  ) => api.get<PaginatedResponse<Ingredient>>(`/suppliers/${id}/ingredients`, { params }),

  priceHistory: (id: number, params?: { start_date?: string; end_date?: string; per_page?: number }) =>
    api.get<PaginatedResponse<IngredientPriceHistory>>(`/suppliers/${id}/price-history`, { params }),

  recalculateRating: (id: number) =>
    api.post<{ message: string; quality_rating: number }>(`/suppliers/${id}/recalculate-rating`),
};

// Recipe Categories API
export const recipeCategoriesApi = {
  list: (params: { restaurant_id: number }) =>
    api.get<{ categories: RecipeCategory[] }>('/recipe-categories', { params }),

  create: (data: { restaurant_id: number; name: string; icon?: string; color?: string }) =>
    api.post<{ message: string; category: RecipeCategory }>('/recipe-categories', data),

  update: (id: number, data: Partial<RecipeCategory>) =>
    api.put<{ message: string; category: RecipeCategory }>(`/recipe-categories/${id}`, data),

  delete: (id: number) =>
    api.delete<{ message: string }>(`/recipe-categories/${id}`),

  reorder: (data: { restaurant_id: number; order: number[] }) =>
    api.post<{ message: string }>('/recipe-categories/reorder', data),
};

// Recipes API
export const recipesApi = {
  list: (params: {
    restaurant_id: number;
    category_id?: number;
    status?: 'draft' | 'active' | 'archived';
    search?: string;
    is_active?: boolean;
    food_cost_status?: 'green' | 'yellow' | 'red';
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<Recipe>>('/recipes', { params }),

  get: (id: number) => api.get<{
    recipe: Recipe;
    escandallo: Escandallo;
  }>(`/recipes/${id}`),

  subRecipes: (params: { restaurant_id: number; exclude_id?: number }) =>
    api.get<{ data: SubRecipeOption[] }>('/recipes-sub-recipes', { params }),

  create: (data: {
    restaurant_id: number;
    name: string;
    description?: string;
    portions: number;
    yield_quantity?: number;
    yield_unit?: string;
    pvp?: number;
    prep_time?: number;
    cook_time?: number;
    difficulty?: 'easy' | 'medium' | 'hard';
    category_id?: number;
    image_url?: string;
    status?: 'draft' | 'active' | 'archived';
    tags?: string[];
    notes?: string;
    ingredients: Array<{
      ingredient_id?: number;
      sub_recipe_id?: number;
      quantity: number;
      unit: string;
      waste_percentage?: number;
      notes?: string;
    }>;
    steps?: Array<{
      description: string;
      duration?: number;
      image_url?: string;
      tips?: string;
    }>;
  }) => api.post<{
    message: string;
    recipe: Recipe;
    escandallo: Escandallo;
  }>('/recipes', data),

  update: (id: number, data: Partial<{
    name: string;
    description: string;
    portions: number;
    yield_quantity: number | null;
    yield_unit: string | null;
    pvp: number;
    prep_time: number;
    cook_time: number;
    difficulty: 'easy' | 'medium' | 'hard';
    category_id: number;
    image_url: string;
    status: 'draft' | 'active' | 'archived';
    tags: string[];
    notes: string;
    is_active: boolean;
    ingredients: Array<{
      ingredient_id?: number;
      sub_recipe_id?: number;
      quantity: number;
      unit: string;
      waste_percentage?: number;
      notes?: string;
    }>;
    steps: Array<{
      description: string;
      duration?: number;
      image_url?: string;
      tips?: string;
    }>;
  }>) => api.put<{
    message: string;
    recipe: Recipe;
    escandallo: Escandallo;
  }>(`/recipes/${id}`, data),

  delete: (id: number) => api.delete<{ message: string }>(`/recipes/${id}`),

  escandallo: (id: number) => api.get<Escandallo>(`/recipes/${id}/escandallo`),

  duplicate: (id: number) => api.post<{
    message: string;
    recipe: Recipe;
    escandallo: Escandallo;
  }>(`/recipes/${id}/duplicate`),

  simulate: (id: number, priceChanges: Record<number, number>) =>
    api.post<{
      ingredients: Array<{
        id: number;
        name: string;
        original_price: number;
        new_price: number;
        price_change: number;
        price_change_percentage: number;
        quantity: number;
        net_cost: number;
      }>;
      original_total_cost: number;
      simulated_total_cost: number;
      cost_change: number;
      original_cost_per_portion: number;
      simulated_cost_per_portion: number;
      original_food_cost_percentage: number | null;
      simulated_food_cost_percentage: number | null;
      original_status: string;
      simulated_status: string;
    }>(`/recipes/${id}/simulate`, { price_changes: priceChanges }),

  suggestPvp: (id: number, targetFoodCost?: number) =>
    api.get<{
      current_pvp: number | null;
      current_food_cost_percentage: number | null;
      target_food_cost_percentage: number;
      suggested_pvp: number | null;
      cost_per_portion: number;
    }>(`/recipes/${id}/suggest-pvp`, { params: { target_food_cost: targetFoodCost } }),
};

// Dashboard Types
export interface DashboardStats {
  total_recipes: number;
  total_ingredients: number;
  avg_food_cost: number | null;
  total_potential_revenue: number;
  total_cost: number;
  recipes_by_status: {
    green: number;
    yellow: number;
    red: number;
  };
  recent_price_changes: number;
}

export interface RecipeSummary {
  id: number;
  name: string;
  pvp: number | null;
  cost_per_portion: number;
  food_cost_percentage: number | null;
  food_cost_status: 'green' | 'yellow' | 'red' | 'unknown';
  profit_margin: number | null;
}

export interface Alert {
  id: string;
  type: 'recipe_unprofitable' | 'ingredient_price_spike' | 'food_cost_high' | 'trial_expiring' | 'low_margin_recipes';
  priority: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  action_url?: string;
  action_text?: string;
  created_at: string;
  recipe_id?: number;
  recipe_name?: string;
  ingredient_id?: number;
  ingredient_name?: string;
  food_cost?: number;
  price_change?: number;
  days_left?: number;
}

export interface ProfitabilityReport {
  summary: {
    total_recipes: number;
    total_revenue: number;
    total_cost: number;
    total_profit: number;
    avg_margin: number;
  };
  by_status: {
    green: { count: number; total_revenue: number; total_cost: number; total_profit: number; avg_margin: number; recipes: RecipeSummary[] };
    yellow: { count: number; total_revenue: number; total_cost: number; total_profit: number; avg_margin: number; recipes: RecipeSummary[] };
    red: { count: number; total_revenue: number; total_cost: number; total_profit: number; avg_margin: number; recipes: RecipeSummary[] };
  };
}

export interface IngredientCostReport {
  summary: {
    total_ingredients: number;
    total_cost_impact: number;
    unused_count: number;
  };
  top_cost_ingredients: Array<{
    id: number;
    name: string;
    current_price: number;
    unit: string;
    usage_count: number;
    total_cost_impact: number;
    cost_percentage: number;
    price_trend: number | null;
    waste_percentage: number;
  }>;
  price_increases: Array<{
    id: number;
    name: string;
    price_trend: number;
  }>;
  unused_ingredients: Array<{
    id: number;
    name: string;
  }>;
}

export interface RecipesRanking {
  summary: {
    total_recipes: number;
    avg_food_cost: number | null;
    avg_margin: number | null;
    total_potential_revenue: number;
    total_cost: number;
  };
  ranking: Array<{
    id: number;
    name: string;
    pvp: number | null;
    portions: number;
    total_cost: number;
    cost_per_portion: number;
    food_cost_percentage: number | null;
    food_cost_status: 'green' | 'yellow' | 'red' | 'unknown';
    profit_margin: number | null;
    profit_per_portion: number;
    ingredients_count: number;
  }>;
}

// Dashboard API
export const dashboardApi = {
  stats: (restaurantId: number) =>
    api.get<{
      stats: DashboardStats;
      top_recipes: RecipeSummary[];
      worst_recipes: RecipeSummary[];
    }>('/dashboard/stats', { params: { restaurant_id: restaurantId } }),

  alerts: (restaurantId: number) =>
    api.get<{
      alerts: Alert[];
      count: {
        total: number;
        high: number;
        medium: number;
        low: number;
      };
    }>('/dashboard/alerts', { params: { restaurant_id: restaurantId } }),
};

// Reports API
export const reportsApi = {
  profitability: (restaurantId: number) =>
    api.get<ProfitabilityReport>('/reports/profitability', { params: { restaurant_id: restaurantId } }),

  ingredientsCost: (restaurantId: number) =>
    api.get<IngredientCostReport>('/reports/ingredients-cost', { params: { restaurant_id: restaurantId } }),

  recipesRanking: (restaurantId: number, sortBy?: 'profitability' | 'cost' | 'margin') =>
    api.get<RecipesRanking>('/reports/recipes-ranking', { params: { restaurant_id: restaurantId, sort_by: sortBy } }),
};

// Menu Types
export interface MenuRecipe {
  id: number;
  name: string;
  portions: number;
  course: 'starter' | 'main' | 'dessert' | 'side' | 'beverage';
  cost_per_portion: number;
  total_cost: number;
}

export interface Menu {
  id: number;
  restaurant_id: number;
  name: string;
  description: string | null;
  date_from: string | null;
  date_to: string | null;
  type: 'daily' | 'weekly' | 'special' | 'catering';
  pvp: number | null;
  is_active: boolean;
  total_cost: number;
  cost_per_person: number | null;
  food_cost_percentage: number | null;
  food_cost_status: 'green' | 'yellow' | 'red' | 'unknown';
  recipes_count: number;
  recipes: MenuRecipe[];
  created_at: string;
  updated_at: string;
}

export interface IngredientNeeded {
  ingredient_id: number;
  name: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_cost: number;
}

// Order Types
export interface OrderItem {
  id: number;
  order_id: number;
  ingredient_id: number;
  quantity: number;
  unit: string;
  unit_price: number;
  total_price: number;
  received_quantity: number | null;
  notes: string | null;
  ingredient?: Ingredient;
}

export interface Order {
  id: number;
  restaurant_id: number;
  supplier_id: number;
  order_number: string;
  status: 'draft' | 'pending' | 'confirmed' | 'delivered' | 'cancelled';
  order_date: string;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  subtotal: number;
  tax: number;
  total: number;
  notes: string | null;
  supplier?: Supplier;
  items: OrderItem[];
  created_at: string;
  updated_at: string;
}

// Menus API
export const menusApi = {
  list: (params: {
    restaurant_id: number;
    type?: string;
    is_active?: 0 | 1;
    current?: boolean;
    search?: string;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<Menu>>('/menus', { params }),

  get: (id: number) => api.get<{
    menu: Menu;
    ingredients_needed: IngredientNeeded[];
  }>(`/menus/${id}`),

  create: (data: {
    restaurant_id: number;
    name: string;
    description?: string;
    date_from?: string;
    date_to?: string;
    type?: string;
    pvp?: number;
    is_active?: boolean;
    recipes?: Array<{
      recipe_id: number;
      portions: number;
      course?: string;
      notes?: string;
    }>;
  }) => api.post<{ message: string; menu: Menu }>('/menus', data),

  update: (id: number, data: Partial<{
    name: string;
    description: string;
    date_from: string;
    date_to: string;
    type: string;
    pvp: number;
    is_active: boolean;
    recipes: Array<{
      recipe_id: number;
      portions: number;
      course?: string;
      notes?: string;
    }>;
  }>) => api.put<{ message: string; menu: Menu }>(`/menus/${id}`, data),

  delete: (id: number) => api.delete<{ message: string }>(`/menus/${id}`),

  duplicate: (id: number) => api.post<{ message: string; menu: Menu }>(`/menus/${id}/duplicate`),

  generateOrder: (id: number, supplierId: number, multiplier?: number) =>
    api.post<{
      menu_id: number;
      menu_name: string;
      supplier_id: number;
      multiplier: number;
      items: Array<{
        ingredient_id: number;
        name: string;
        quantity: number;
        unit: string;
        unit_price: number;
        total_price: number;
      }>;
      total: number;
    }>(`/menus/${id}/generate-order`, { supplier_id: supplierId, multiplier }),
};

// Orders API
export const ordersApi = {
  list: (params: {
    restaurant_id: number;
    supplier_id?: number;
    status?: string;
    date_from?: string;
    date_to?: string;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<Order>>('/orders', { params }),

  get: (id: number) => api.get<{ order: Order }>(`/orders/${id}`),

  create: (data: {
    restaurant_id: number;
    supplier_id: number;
    order_date: string;
    expected_delivery_date?: string;
    notes?: string;
    items: Array<{
      ingredient_id: number;
      quantity: number;
      unit: string;
      unit_price: number;
      notes?: string;
    }>;
  }) => api.post<{ message: string; order: Order }>('/orders', data),

  update: (id: number, data: Partial<{
    supplier_id: number;
    order_date: string;
    expected_delivery_date: string;
    notes: string;
    items: Array<{
      ingredient_id: number;
      quantity: number;
      unit: string;
      unit_price: number;
      notes?: string;
    }>;
  }>) => api.put<{ message: string; order: Order }>(`/orders/${id}`, data),

  delete: (id: number) => api.delete<{ message: string }>(`/orders/${id}`),

  confirm: (id: number) => api.post<{ message: string; order: Order }>(`/orders/${id}/confirm`),

  deliver: (id: number, data?: {
    update_prices?: boolean;
    received_items?: Array<{
      item_id: number;
      received_quantity: number;
    }>;
  }) => api.post<{ message: string; order: Order }>(`/orders/${id}/deliver`, data),

  cancel: (id: number) => api.post<{ message: string; order: Order }>(`/orders/${id}/cancel`),

  fromMenu: (data: {
    restaurant_id: number;
    menu_id: number;
    supplier_id: number;
    multiplier?: number;
    order_date: string;
    expected_delivery_date?: string;
  }) => api.post<{ message: string; order: Order }>('/orders/from-menu', data),

  compareSuppliers: (restaurantId: number, ingredientIds: number[]) =>
    api.post<{
      comparison: Array<{
        ingredient_id: number;
        name: string;
        unit: string;
        current_price: number;
        default_supplier: string | null;
        suppliers: Array<{
          supplier_id: number;
          latest_price: number;
          avg_price: number;
          min_price: number;
          max_price: number;
        }>;
      }>;
    }>('/orders/compare-suppliers', { restaurant_id: restaurantId, ingredient_ids: ingredientIds }),
};

// Subscription Types
export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  original_price: number;
  is_custom_price: boolean;
  price_id: string | null;
  users: number;
  ocr_scans: number;
  menu_scans: number;
  features: {
    ingredients: boolean;
    recipes: boolean;
    menus: boolean;
    orders: boolean;
    market_comparison: boolean;
    dietary_restrictions: boolean;
    catering: boolean;
    carta_designer: boolean;
    carta_express: boolean;
    time_tracking: boolean;
    multi_restaurant: boolean;
    ai_optimizer: boolean;
  };
}

export interface SubscriptionDetails {
  id: number;
  paddle_id: string;
  status: string;
  is_active: boolean;
  is_cancelled: boolean;
  is_on_grace_period: boolean;
  is_past_due: boolean;
  is_paused: boolean;
  ends_at: string | null;
  paused_at: string | null;
}

export interface SubscriptionStatus {
  has_subscription: boolean;
  on_trial: boolean;
  trial_ended: boolean;
  trial_days_remaining: number;
  trial_ends_at: string | null;
  trial_warning_level: 'warning' | 'critical' | 'urgent' | null;
  in_grace_period: boolean;
  active_plan: string | null;
  plan_config: SubscriptionPlan | null;
  subscription: SubscriptionDetails | null;
  is_past_due: boolean;
  has_custom_price: boolean;
  custom_price: { amount: number; note: string | null } | null;
  provider?: 'paddle' | 'polar';
}

export interface Transaction {
  id: number | string;
  paddle_id?: string;
  provider_id?: string;
  status: string;
  total: string;
  tax: string;
  currency: string;
  billed_at: string | null;
  created_at: string | null;
  receipt_url: string | null;
}

export interface CheckoutData {
  transaction_id?: string;
  price_id?: string;
  checkout_url?: string;
  checkout_id?: string;
  custom_data?: Record<string, string>;
}

// AI Types
export interface AiGeneratedIngredient {
  name: string;
  quantity: number;
  unit: string;
  existing_ingredient_id: number | null;
  new_ingredient: boolean;
}

export interface AiGeneratedStep {
  description: string;
  duration: number | null;
  tips: string | null;
}

export interface AiGeneratedRecipe {
  name: string;
  description: string;
  portions: number;
  prep_time: number | null;
  cook_time: number | null;
  difficulty: 'easy' | 'medium' | 'hard';
  ingredients: AiGeneratedIngredient[];
  steps: AiGeneratedStep[];
  tags: string[];
}

export interface AiDraft {
  id: number;
  user_id: number;
  restaurant_id: number;
  prompt: string;
  name: string;
  description: string | null;
  portions: number;
  ingredients: AiGeneratedIngredient[];
  steps: AiGeneratedStep[] | null;
  difficulty: 'easy' | 'medium' | 'hard';
  prep_time: number | null;
  cook_time: number | null;
  status: 'draft' | 'converted' | 'discarded';
  created_at: string;
}

export interface OcrExtractedItem {
  name: string;
  quantity: number | null;
  unit: string | null;
  unit_price: number | null;
  total_price: number | null;
  extracted_from_name?: boolean; // Flag indicating quantity was extracted from product name
  presentation?: string; // Optional display format like "6 × 1L"
  container_count?: number; // Number of containers on the line (quantity is the line total)
  needs_review?: boolean; // Backend validation flagged this line as untrustworthy
  review_reason?: string; // Why it was flagged, shown next to the line
}

export interface OcrExtractedData {
  supplier: string | null;
  invoice_number: string | null;
  date: string | null;
  items: OcrExtractedItem[];
  subtotal: number | null;
  tax: number | null;
  total: number | null;
  error?: string;
}

export interface OcrMatchedItem {
  extracted: OcrExtractedItem;
  matched_ingredient: {
    id: number;
    name: string;
    unit: string;
    current_price: number;
  } | null;
  confidence: number;
}

export interface OcrScan {
  id: number;
  user_id: number;
  restaurant_id: number;
  scan_type: string;
  filename: string;
  status: 'processing' | 'completed' | 'failed';
  items_extracted: number | null;
  items_matched: number | null;
  total_amount: number | null;
  error_message: string | null;
  pages_count?: number;
  created_at: string;
}

export interface OcrUsage {
  limit: number;
  used: number;
  remaining: number;
  is_unlimited: boolean;
  plan: string | null;
}

// AI API
export const aiApi = {
  generateRecipe: (data: {
    restaurant_id: number;
    description: string;
    portions?: number;
  }) => api.post<{
    message: string;
    draft: AiDraft;
    recipe: AiGeneratedRecipe;
  }>('/ai/generate-recipe', data),

  convertDraftToRecipe: (draftId: number, data: {
    name?: string;
    description?: string;
    portions?: number;
    pvp?: number;
    ingredients: Array<{
      ingredient_id: number;
      quantity: number;
      unit: string;
      waste_percentage?: number;
    }>;
  }) => api.post<{
    message: string;
    recipe: Recipe;
  }>(`/ai/drafts/${draftId}/convert`, data),

  listDrafts: () => api.get<{ drafts: AiDraft[] }>('/ai/drafts'),

  deleteDraft: (draftId: number) => api.delete<{ message: string }>(`/ai/drafts/${draftId}`),

  scanInvoice: (data: FormData) => api.post<{
    message: string;
    scan: OcrScan;
    extracted: OcrExtractedData;
    matches: OcrMatchedItem[];
    warnings: string[];
    ocr_remaining?: number;
  }>('/ai/scan-invoice', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  applyOcrPrices: (data: {
    scan_id: number;
    prices: Array<{
      ingredient_id?: number;
      price: number;
      quantity?: number;
      date?: string;
      extracted_name?: string;
      extracted_unit?: string;
    }>;
    supplier_id?: number;
    supplier_name?: string;
    reference?: string;
    update_stock?: boolean;
  }) => api.post<{
    message: string;
    ingredients: Ingredient[];
    created_count?: number;
    updated_count?: number;
    supplier?: Supplier;
  }>('/ai/apply-ocr-prices', data),

  getOcrUsage: () => api.get<OcrUsage>('/ai/ocr-usage'),

  scanMenu: (data: {
    restaurant_id: number;
    images: Array<{ data: string; mime_type: string }>;
    cuisine_type?: string;
  }) => api.post<{
    message: string;
    scan_id: number;
    sections: MenuScanSection[];
  }>('/ai/scan-menu', data, { timeout: 300000 }), // 5 min — AI processes each dish

  executeMenuScan: (scanId: number, data: {
    sections: MenuScanSection[];
  }) => api.post<{
    message: string;
    carta_id: number;
    carta_hash: string;
    recipes_created: number;
    ingredients_created: number;
  }>(`/ai/scan-menu/${scanId}/execute`, data),

  discardMenuScan: (scanId: number) =>
    api.delete<{ message: string }>(`/ai/scan-menu/${scanId}`),

  optimizeRecipe: (data: { recipe_id: number; pvp: number }) =>
    api.post<{
      suggestions: AiOptimizationSuggestion[];
      recipe_id: number;
      pvp: number;
    }>('/ai/optimize-recipe', data),
};

export interface AiOptimizationSuggestion {
  type: 'price_increase' | 'ingredient_swap' | 'quantity_reduce' | 'supplier_change' | 'portion_adjust';
  title: string;
  reasoning: string;
  action: string;
  impact_euros: number;
  new_food_cost: number;
  priority: 'high' | 'medium' | 'low';
}

export interface MenuScanIngredient {
  name: string;
  quantity: number;
  unit: string;
}

export interface MenuScanItem {
  name: string;
  description?: string;
  price?: number | null;
  enabled: boolean;
  ingredients: MenuScanIngredient[];
}

export interface MenuScanSection {
  name: string;
  items: MenuScanItem[];
}

export interface MenuScanResult {
  scan_id: number;
  sections: MenuScanSection[];
}

// Market Price Types
export interface MarketPrice {
  product_name: string;
  category: string;
  subcategory: string | null;
  source: 'mercasa' | 'mercabarna' | 'pleko_aggregate' | 'manual';
  market: string;
  price: number;
  price_min: number | null;
  price_max: number | null;
  unit: string;
  price_date: string;
}

export interface MarketPriceComparison {
  ingredient: {
    id: number;
    name: string;
    current_price: number;
    unit: string;
    supplier: string | null;
    matched_names?: string[];
  };
  market_prices: Array<{
    market: string;
    market_name: string;
    price: number;
    price_min: number | null;
    price_max: number | null;
    unit: string;
    date: string;
    source: string;
    difference: number | null;
    matched_name?: string;
    matched_score?: number;
  }>;
  best_price: {
    market: string;
    market_name: string;
    price: number;
    matched_name?: string;
    matched_score?: number;
  } | null;
  potential_savings: number | null;
  potential_savings_percent: number | null;
  last_updated: string | null;
}

export interface MarketPriceTrend {
  product: string;
  period_days: number;
  data: Record<string, Record<string, {
    price: number;
    price_min: number | null;
    price_max: number | null;
  }>>;
}

export interface MarketCategoryStats {
  category: string;
  products_count: number;
  avg_price: number;
  min_price: number;
  max_price: number;
}

export interface MarketInfo {
  id: string;
  name: string;
}

export interface BulkComparisonSummary {
  ingredients_count: number;
  total_current_cost: number;
  total_best_price: number;
  potential_savings: number;
  potential_savings_percent: number;
}

// Market Prices API
export const marketPricesApi = {
  list: (params?: {
    category?: string;
    market?: string;
    source?: string;
    search?: string;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<MarketPrice>>('/market-prices', { params }),

  search: (q: string, params?: { category?: string; market?: string }) =>
    api.get<{
      query: string;
      results: Array<{
        product_name: string;
        category: string;
        subcategory: string | null;
        latest_price: number;
        unit: string;
        market: string;
        date: string;
        markets_count: number;
      }>;
      count: number;
    }>('/market-prices/search', { params: { q, ...params } }),

  trends: (product: string, params?: { days?: number; market?: string }) =>
    api.get<MarketPriceTrend>('/market-prices/trends', { params: { product, ...params } }),

  categories: () =>
    api.get<{ categories: MarketInfo[] }>('/market-prices/categories'),

  categoryStats: (market?: string) =>
    api.get<{ market: string; categories: MarketCategoryStats[] }>('/market-prices/category-stats', { params: { market } }),

  markets: () =>
    api.get<{ markets: MarketInfo[] }>('/market-prices/markets'),

  compareIngredient: (ingredientId: number) =>
    api.get<MarketPriceComparison>(`/market-prices/ingredient/${ingredientId}/compare`),

  bulkCompare: (ingredientIds: number[]) =>
    api.post<{
      comparisons: MarketPriceComparison[];
      summary: BulkComparisonSummary;
    }>('/market-prices/bulk-compare', { ingredient_ids: ingredientIds }),

  importManual: (data: {
    product_name: string;
    category: string;
    subcategory?: string;
    price: number;
    price_min?: number;
    price_max?: number;
    unit?: string;
    market?: string;
    price_date?: string;
  }) => api.post<{ message: string; data: MarketPrice }>('/market-prices/import', data),

  sync: (params?: { market?: string; category?: string }) =>
    api.post<{ status: string; message: string; documentation?: string }>('/market-prices/sync', params),
};

// Restaurant Settings API
export const restaurantApi = {
  get: () =>
    api.get<{ data: RestaurantSettings }>('/restaurant'),

  update: (data: Partial<Omit<RestaurantSettings, 'id' | 'created_at' | 'updated_at'>>) =>
    api.put<{ message: string; data: RestaurantSettings }>('/restaurant', data),

  getCurrencies: () =>
    api.get<{ data: Currency[] }>('/restaurant/currencies'),

  getTimezones: () =>
    api.get<{ data: Timezone[] }>('/restaurant/timezones'),

  create: (data: { name: string; city?: string }) =>
    api.post<{ message: string; restaurant: Restaurant }>('/restaurants', data),
};

// Team API
export const teamApi = {
  getTeam: (restaurantId: number) =>
    api.get<TeamInfo>(`/restaurants/${restaurantId}/team`),

  invite: (restaurantId: number, data: { email: string; role: RestaurantRole }) =>
    api.post<{ message: string; invitation: RestaurantInvitation }>(`/restaurants/${restaurantId}/team/invite`, data),

  cancelInvitation: (restaurantId: number, invitationId: number) =>
    api.delete<{ message: string }>(`/restaurants/${restaurantId}/team/invitation/${invitationId}`),

  resendInvitation: (restaurantId: number, invitationId: number) =>
    api.post<{ message: string; invitation: RestaurantInvitation }>(`/restaurants/${restaurantId}/team/invitation/${invitationId}/resend`),

  updateMemberRole: (restaurantId: number, memberId: number, role: RestaurantRole) =>
    api.put<{ message: string; member: RestaurantMember }>(`/restaurants/${restaurantId}/team/${memberId}/role`, { role }),

  removeMember: (restaurantId: number, memberId: number) =>
    api.delete<{ message: string }>(`/restaurants/${restaurantId}/team/${memberId}`),

  leave: (restaurantId: number) =>
    api.post<{ message: string }>(`/restaurants/${restaurantId}/team/leave`),

  transferOwnership: (restaurantId: number, memberId: number) =>
    api.post<{ message: string }>(`/restaurants/${restaurantId}/team/transfer-ownership`, { new_owner_member_id: memberId }),
};

// Invitation API (for accepting/declining invitations)
export const invitationApi = {
  get: (token: string) =>
    api.get<{
      invitation: {
        id: number;
        email: string;
        role: RestaurantRole;
        role_name: string;
        expires_at: string;
        created_at: string;
      };
      restaurant: {
        id: number;
        name: string;
        city: string | null;
      };
      inviter: {
        name: string;
      };
    }>(`/invitations/${token}`),

  accept: (token: string) =>
    api.post<{ message: string; membership: RestaurantMember }>(`/invitations/${token}/accept`),

  decline: (token: string) =>
    api.post<{ message: string }>(`/invitations/${token}/decline`),

  pending: () =>
    api.get<{ invitations: PendingInvitation[] }>('/invitations/pending'),
};

export const subscriptionApi = {
  getStatus: () =>
    api.get<SubscriptionStatus>('/subscription/status'),

  getPlans: () =>
    api.get<{ plans: SubscriptionPlan[] }>('/subscription/plans'),

  createCheckout: (plan: string, billingPeriod: 'monthly' | 'annual' = 'monthly') =>
    api.post<CheckoutData>('/subscription/checkout', { plan, billing_period: billingPeriod }),

  createSetupFeeCheckout: () =>
    api.post<{ provider: string; checkout_url: string; checkout_id: string; type: string }>('/subscription/setup-fee-checkout'),

  cancel: () =>
    api.post<{ message: string; ends_at?: string }>('/subscription/cancel'),

  resume: () =>
    api.post<{ message: string }>('/subscription/resume'),

  changePlan: (plan: string, billingPeriod: 'monthly' | 'annual' = 'monthly') =>
    api.put<{ message: string; new_plan: string }>('/subscription/plan', { plan, billing_period: billingPeriod }),

  getTransactions: () =>
    api.get<{ transactions: Transaction[] }>('/subscription/transactions'),

  getUpdatePaymentMethodUrl: () =>
    api.get<{ url: string }>('/subscription/update-payment-method'),
};

// Admin Types
export interface AdminOverviewStats {
  users: {
    total: number;
    active_trials: number;
    paid: number;
    trials_expiring_soon: number;
  };
  signups: {
    today: number;
    this_week: number;
    this_month: number;
  };
  revenue: {
    mrr: number;
    arr: number;
  };
  conversion: {
    rate: number;
    converted: number;
    expired_trials: number;
  };
  churn: {
    last_30_days: number;
  };
}

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  created_at: string;
  trial_ends_at: string | null;
  trial_days_remaining: number;
  on_trial: boolean;
  status: 'trial' | 'paid' | 'expired' | 'inactive' | 'churned';
  plan: string | null;
  is_subscribed: boolean;
  subscription_status: string | null;
  has_custom_price: boolean;
  custom_monthly_price: number | null;
  mrr: number;
  restaurants_count: number;
  recipes_count: number;
  ingredients_count: number;
  cartas_count: number;
  last_login_at: string | null;
  activity_score: number;
  engagement: 'hot' | 'warm' | 'cold' | 'new';
  is_admin: boolean;
  primary_role: 'owner' | 'admin' | 'chef' | 'viewer' | 'employee' | null;
  restaurant_name: string | null;
  admin_notes: string | null;
  help_videos_watched: number;
  help_videos_total: number;
  setup_fee_status: 'pending' | 'paid' | 'waived';
}

export interface Partner {
  id: number;
  name: string;
  code: string;
  logo_url: string | null;
  discount_percentage: number;
  discount_base: 'original' | 'current';
  allowed_plans: string[] | null;
  polar_discount_id: string | null;
  polar_discount_ids: Record<string, string> | null;
  contact_name: string | null;
  contact_email: string | null;
  notes: string | null;
  is_active: boolean;
  max_redemptions: number | null;
  signups: number;
  active_subscribers: number;
  clicks_count: number;
  price_breakdown: Record<string, { target_price: number; effective_discount: number }>;
  created_at: string;
}

export interface PartnerUser {
  id: number;
  name: string;
  email: string;
  created_at: string;
  status: 'trial' | 'subscribed' | 'expired';
}

export interface PartnerClickEntry {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  url: string;
  clicked_at: string;
}

export interface PartnerValidation {
  valid: boolean;
  reason?: string;
  partner?: {
    name: string;
    code: string;
    logo_url: string | null;
    discount_percentage: number;
    allowed_plans: string[] | null;
  };
}

export interface OnlineUser {
  id: number;
  name: string;
  email: string;
  is_admin: boolean;
}

export interface AdminUserDetail {
  user: {
    id: number;
    name: string;
    email: string;
    phone: string | null;
    created_at: string;
    email_verified_at: string | null;
    trial_ends_at: string | null;
    trial_days_remaining: number;
    on_trial: boolean;
    trial_ended: boolean;
    admin_notes: string | null;
    custom_monthly_price: number | null;
    custom_price_note: string | null;
    setup_fee_status: 'pending' | 'paid' | 'waived';
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    referrer: string | null;
  };
  subscription: SubscriptionStatus;
  restaurants: Array<{
    id: number;
    name: string;
    city: string | null;
    members_count: number;
    created_at: string;
  }>;
  audit_logs: AdminAuditLog[];
  activity_logs: UserActivityLog[];
}

export interface UserActivityLog {
  id: number;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  entity_name: string | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
  restaurant: { id: number; name: string } | null;
}

export interface AdminAuditLog {
  id: number;
  action: string;
  target_type: string | null;
  target_id: number | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  notes: string | null;
  ip_address: string | null;
  created_at: string;
  admin: {
    id?: number;
    name: string;
    email?: string;
  };
}

export interface MarketScrapeRun {
  id: number;
  source: string;
  status: 'running' | 'success' | 'error';
  started_at: string | null;
  finished_at: string | null;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  error_message: string | null;
  source_url: string | null;
  metadata: Record<string, unknown> | null;
}

export interface MarketScrapePrice {
  id: number;
  product_name: string;
  category: string;
  subcategory: string | null;
  source: string;
  market: string;
  price: string;
  price_min: string | null;
  price_max: string | null;
  unit: string;
  price_date: string;
}

export interface SignupTrend {
  date: string;
  count: number;
}

export interface RevenueTrend {
  month: string;
  label: string;
  mrr: number;
  subscriptions: number;
}

export interface TrialAnalytics {
  active: number;
  expired_not_converted: number;
  converted: number;
  total: number;
  conversion_rate: number;
}

export interface PlanDistribution {
  [key: string]: {
    count: number;
    revenue: number;
  };
}

// Admin API
export const adminApi = {
  // Overview & Analytics
  overview: () =>
    api.get<AdminOverviewStats>('/admin/overview'),

  onlineUsers: () =>
    api.get<{ count: number; users: OnlineUser[] }>('/admin/online-users'),

  signupTrends: (days = 30) =>
    api.get<{ trends: SignupTrend[] }>('/admin/analytics/signups', { params: { days } }),

  revenueTrends: (months = 12) =>
    api.get<{ trends: RevenueTrend[] }>('/admin/analytics/revenue', { params: { months } }),

  trialAnalytics: () =>
    api.get<TrialAnalytics>('/admin/analytics/trials'),

  planDistribution: () =>
    api.get<{ distribution: PlanDistribution }>('/admin/analytics/plans'),

  expiringTrials: (days = 7) =>
    api.get<{ users: AdminUser[] }>('/admin/expiring-trials', { params: { days } }),

  auditLogs: (limit = 50) =>
    api.get<{ logs: AdminAuditLog[] }>('/admin/audit-logs', { params: { limit } }),

  marketScrapeRuns: (params?: {
    source?: string;
    status?: string;
    search?: string;
    per_page?: number;
    page?: number;
  }) =>
    api.get<{ data: MarketScrapeRun[]; meta?: unknown }>('/admin/market-scrapes', { params }),

  marketScrapeRun: (id: number) =>
    api.get<MarketScrapeRun>(`/admin/market-scrapes/${id}`),

  marketScrapePrices: (id: number, params?: {
    search?: string;
    market?: string;
    category?: string;
    per_page?: number;
    page?: number;
  }) =>
    api.get<{
      run: MarketScrapeRun;
      prices: {
        data: MarketScrapePrice[];
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
      };
    }>(`/admin/market-scrapes/${id}/prices`, { params }),

  // User Management
  getUsers: (params: {
    search?: string;
    status?: string;
    plan?: string;
    has_custom_price?: boolean;
    sort_by?: string;
    sort_dir?: string;
    per_page?: number;
    page?: number;
  }) =>
    api.get<{
      users: AdminUser[];
      pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
      };
    }>('/admin/users', { params }),

  getUser: (id: number) =>
    api.get<AdminUserDetail>(`/admin/users/${id}`),

  getUserActivity: (id: number, limit = 50) =>
    api.get<{ activity: UserActivityLog[] }>(`/admin/users/${id}/activity`, { params: { limit } }),

  updateUser: (id: number, data: { admin_notes?: string }) =>
    api.put<{ message: string; user: AdminUser }>(`/admin/users/${id}`, data),

  extendTrial: (id: number, days: number, reason?: string) =>
    api.post<{ message: string; user: AdminUser }>(`/admin/users/${id}/extend-trial`, { days, reason }),

  setCustomPrice: (id: number, price: number, note?: string) =>
    api.post<{ message: string; user: AdminUser }>(`/admin/users/${id}/custom-price`, { price, note }),

  removeCustomPrice: (id: number) =>
    api.delete<{ message: string; user: AdminUser }>(`/admin/users/${id}/custom-price`),

  cancelSubscription: (id: number, reason?: string, immediately = false) =>
    api.post<{ message: string; user: AdminUser }>(`/admin/users/${id}/cancel-subscription`, { reason, immediately }),

  forceVerifyEmail: (id: number) =>
    api.post<{ message: string; user: AdminUser }>(`/admin/users/${id}/verify-email`, {}),

  impersonate: (id: number) =>
    api.post<{ token: string; user: { id: number; name: string; email: string } }>(`/admin/users/${id}/impersonate`, {}),

  waiveSetupFee: (id: number) =>
    api.post<{ message: string; setup_fee_status: string }>(`/admin/users/${id}/waive-setup-fee`, {}),

  getCustomPriceUsers: () =>
    api.get<{ users: AdminUser[]; total_custom_revenue: number }>('/admin/custom-prices'),

  // Market Scraping Control
  getScrapingStatus: () =>
    api.get<{ mercasa: boolean; observatorio: boolean }>('/admin/market-scrapes/status/enabled'),

  toggleScraping: (scraper: 'mercasa' | 'observatorio', enabled: boolean) =>
    api.post<{ success: boolean; scraper: string; enabled: boolean; message: string }>('/admin/market-scrapes/toggle', { scraper, enabled }),

  // Partners
  getPartners: () =>
    api.get<{ partners: Partner[] }>('/admin/partners'),

  getPartner: (id: number) =>
    api.get<{ partner: Partner; users: PartnerUser[] }>(`/admin/partners/${id}`),

  createPartner: (data: Partial<Partner> & { sync_polar?: boolean }) =>
    api.post<{ message: string; partner: Partner }>('/admin/partners', data),

  updatePartner: (id: number, data: Partial<Partner>) =>
    api.put<{ message: string; partner: Partner }>(`/admin/partners/${id}`, data),

  deletePartner: (id: number) =>
    api.delete<{ message: string }>(`/admin/partners/${id}`),

  syncPartnerPolar: (id: number) =>
    api.post<{ message: string; polar_discount_id: string }>(`/admin/partners/${id}/sync-polar`),

  getPartnerClicks: (id: number) =>
    api.get<{
      clicks: PartnerClickEntry[];
      total_clicks: number;
      unique_users: number;
    }>(`/admin/partners/${id}/clicks`),
};

// Public partner validation (no auth)
export const partnerApi = {
  validate: (code: string) =>
    api.get<PartnerValidation>(`/partners/validate/${code}`),
};

// Track partner link clicks (fire-and-forget)
export const trackPartnerClick = (partnerName: string, url: string) => {
  api.post('/partner-clicks', { partner_name: partnerName, url }).catch(() => {});
};

// Outreach Lead Types
export interface OutreachLead {
  id: number;
  name: string | null;
  restaurant_name: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  instagram_url: string | null;
  cuisine_type: string | null;
  zone: string | null;
  province: string | null;
  google_rating: number | null;
  google_reviews: number | null;
  address: string | null;
  status: 'nuevo' | 'contactado' | 'respondio' | 'registrado' | 'no_interesado' | 'bounced';
  email_sent_at: string | null;
  instagram_dm_sent_at: string | null;
  wa_sent_at: string | null;
  has_whatsapp: boolean | null;
  detected_language: string | null;
  email_message_id: string | null;
  email_delivered_at: string | null;
  email_opened_at: string | null;
  email_clicked_at: string | null;
  email_bounced_at: string | null;
  email_bounce_reason: string | null;
  email_subject: string | null;
  email_body: string | null;
  response_received_at: string | null;
  response_content: string | null;
  reply_sent: boolean;
  followup_count: number;
  last_followup_at: string | null;
  pleko_user_id: number | null;
  notes: string | null;
  web_analysis: {
    web_type?: string;
    is_responsive?: boolean;
    has_menu_online?: boolean;
    has_reservas?: boolean;
    has_whatsapp?: boolean;
    has_delivery?: boolean;
    has_social_media?: string[];
    booking_system?: string;
    pos_detected?: string;
    cms?: string;
    has_google_analytics?: boolean;
    has_cookie_banner?: boolean;
    has_ssl?: boolean;
    languages?: string[];
    design_quality?: number;
    seo_quality?: number;
    digital_maturity_score?: number;
    detected_tools?: string[];
    weaknesses?: string[];
    opportunities?: string[];
  } | null;
  digital_score: number | null;
  opportunities: string | null;
  created_at: string;
  updated_at: string;
}

export interface OutreachStats {
  total: number;
  by_status: Record<string, number>;
  today_new: number;
  today_contacted: number;
  registrados: number;
  respondieron: number;
  conversion_rate: number;
}

export const outreachApi = {
  list: (params?: {
    search?: string;
    status?: string;
    zone?: string;
    per_page?: number;
    page?: number;
  }) =>
    api.get<{
      data: OutreachLead[];
      zones: string[];
      pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
      };
    }>('/admin/outreach', { params }),

  stats: () =>
    api.get<OutreachStats>('/admin/outreach/stats'),

  update: (id: number, data: Partial<OutreachLead>) =>
    api.put<{ message: string; lead: OutreachLead }>(`/admin/outreach/${id}`, data),

  updateStatus: (id: number, status: OutreachLead['status']) =>
    api.put<{ message: string; lead: OutreachLead }>(`/admin/outreach/${id}/status`, { status }),

  sendEmail: (id: number, data: { subject: string; body: string }) =>
    api.post<{ message: string; lead: OutreachLead; message_id: string }>(`/admin/outreach/${id}/send-email`, data),

  emailHistory: (params?: { search?: string; email_status?: string; page?: number; per_page?: number }) =>
    api.get<{
      data: Array<{
        id: number;
        restaurant_name: string | null;
        email: string | null;
        zone: string | null;
        status: string;
        email_subject: string | null;
        email_body: string | null;
        email_message_id: string | null;
        email_sent_at: string | null;
        email_delivered_at: string | null;
        email_opened_at: string | null;
        email_clicked_at: string | null;
        email_bounced_at: string | null;
        email_bounce_reason: string | null;
        response_received_at: string | null;
        response_content: string | null;
      }>;
      pagination: { current_page: number; last_page: number; per_page: number; total: number };
      summary: { total: number; delivered: number; opened: number; bounced: number; replied: number; open_rate: number; bounce_rate: number };
    }>('/admin/outreach/email-history', { params }),

  delete: (id: number) =>
    api.delete<{ message: string }>(`/admin/outreach/${id}`),
};

// Despiece Types
export interface Despiece {
  id: number;
  restaurant_id: number;
  name: string;
  description: string | null;
  ingredient_id: number;
  input_quantity: number;
  input_unit: string;
  input_total_cost: number;
  allocation_method: 'weight' | 'value';
  notes: string | null;
  status: 'draft' | 'active' | 'archived';
  waste_percentage: number;
  usable_percentage: number;
  total_output_quantity: number;
  total_usable_quantity: number;
  ingredient?: Ingredient;
  outputs?: DespieceOutput[];
  created_at: string;
  updated_at: string;
}

export interface DespieceOutput {
  id: number;
  despiece_id: number;
  name: string;
  quantity: number;
  unit: string;
  value_per_unit: number | null;
  is_waste: boolean;
  linked_ingredient_id: number | null;
  sort_order: number;
  notes: string | null;
  allocated_cost: number;
  cost_per_unit: number;
  weight_percentage: number;
  linked_ingredient?: Ingredient;
}

// Carta Types (Menu Designer)
export interface CartaDesignSettings {
  template: 'classic' | 'modern' | 'elegant' | 'rustic' | 'minimal';
  colors: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
    accent: string;
    price: string;
  };
  fonts: {
    heading: string;
    body: string;
    price: string;
  };
  layout: {
    columns: 1 | 2;
    showImages: boolean;
    imagePosition: 'left' | 'right' | 'top';
    showDescriptions: boolean;
    showAllergens: boolean;
    showDietaryIcons: boolean;
    priceAlignment: 'left' | 'right' | 'dotted-line';
  };
  branding: {
    logoUrl: string | null;
    logoPosition: 'top-left' | 'top-center' | 'top-right';
    showRestaurantName: boolean;
    tagline: string | null;
    footerText: string | null;
  };
  page: {
    size: 'A4' | 'letter' | 'A5';
    orientation: 'portrait' | 'landscape';
    margins: { top: number; right: number; bottom: number; left: number };
  };
}

export interface AllergenInfo {
  icon: string;
  label: string;
}

export interface DietaryIconInfo {
  icon: string;
  label: string;
  color: string;
}

export interface CartaItemVariant {
  name: string;
  price: number;
}

export interface CartaTranslations {
  [locale: string]: {
    name?: string | null;
    description?: string | null;
  };
}

export interface CartaItem {
  id: number;
  carta_section_id: number;
  recipe_id: number | null;
  recipe?: Recipe;
  name: string;
  description: string | null;
  price: number;
  allergens: string[];
  dietary_icons: string[];
  image_url: string | null;
  is_featured: boolean;
  is_available: boolean;
  sort_order: number;
  variants: CartaItemVariant[] | null;
  allergen_labels?: AllergenInfo[];
  dietary_icon_labels?: DietaryIconInfo[];
  formatted_price?: string;
  translations?: CartaTranslations;
}

export interface CartaSection {
  id: number;
  carta_id: number;
  name: string;
  description: string | null;
  sort_order: number;
  is_visible: boolean;
  style_overrides: Record<string, unknown> | null;
  items: CartaItem[];
  items_count?: number;
  translations?: CartaTranslations;
}

export interface CartaTranslationStatus {
  [locale: string]: {
    total: number;
    translated: number;
    complete: boolean;
  };
}

export interface Carta {
  id: number;
  restaurant_id: number;
  name: string;
  slug: string;
  public_hash: string;
  description: string | null;
  status: 'draft' | 'published' | 'archived';
  template: string;
  design_settings: CartaDesignSettings;
  default_language: string;
  available_languages: string[];
  is_active: boolean;
  published_at: string | null;
  view_count: number;
  sections: CartaSection[];
  items_count?: number;
  sections_count?: number;
  public_url?: string | null;
  translation_status?: CartaTranslationStatus;
  created_at: string;
  updated_at: string;
}

export interface CartaTemplate {
  name: string;
  description: string;
}

export interface CartaReferenceData {
  allergens: Record<string, AllergenInfo>;
  dietary_icons: Record<string, DietaryIconInfo>;
  templates: Record<string, CartaTemplate>;
  default_design_settings: CartaDesignSettings;
  supported_languages?: Record<string, string>;
}

// Cartas API
export const cartasApi = {
  list: (params: {
    restaurant_id: number;
    status?: 'draft' | 'published' | 'archived';
    search?: string;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<Carta>>('/cartas', { params }),

  get: (id: number) => api.get<{
    carta: Carta;
    allergens: Record<string, AllergenInfo>;
    dietary_icons: Record<string, DietaryIconInfo>;
    templates: Record<string, CartaTemplate>;
  }>(`/cartas/${id}`),

  create: (data: {
    restaurant_id: number;
    name: string;
    description?: string;
    template?: string;
    design_settings?: Partial<CartaDesignSettings>;
  }) => api.post<{ message: string; carta: Carta }>('/cartas', data),

  update: (id: number, data: Partial<{
    name: string;
    description: string;
    template: string;
    design_settings: Partial<CartaDesignSettings>;
    is_active: boolean;
  }>) => api.put<{ message: string; carta: Carta }>(`/cartas/${id}`, data),

  delete: (id: number) => api.delete<{ message: string }>(`/cartas/${id}`),

  duplicate: (id: number) => api.post<{ message: string; carta: Carta }>(`/cartas/${id}/duplicate`),

  publish: (id: number) => api.post<{ message: string; carta: Carta }>(`/cartas/${id}/publish`),

  unpublish: (id: number) => api.post<{ message: string; carta: Carta }>(`/cartas/${id}/unpublish`),

  qrCode: (id: number) => api.get<{
    carta_id: number;
    carta_name: string;
    public_url: string;
    public_hash: string;
    qr_code: string;
  }>(`/cartas/${id}/qr-code`),

  exportPdf: (id: number) => api.post(`/cartas/${id}/export/pdf`, {}, { responseType: 'blob' }),

  referenceData: () => api.get<CartaReferenceData>('/cartas/reference-data'),

  // Section management
  addSection: (cartaId: number, data: { name: string; description?: string }) =>
    api.post<{ message: string; section: CartaSection }>(`/cartas/${cartaId}/sections`, data),

  updateSection: (cartaId: number, sectionId: number, data: Partial<{
    name: string;
    description: string;
    is_visible: boolean;
    style_overrides: Record<string, unknown>;
  }>) => api.put<{ message: string; section: CartaSection }>(`/cartas/${cartaId}/sections/${sectionId}`, data),

  deleteSection: (cartaId: number, sectionId: number) =>
    api.delete<{ message: string }>(`/cartas/${cartaId}/sections/${sectionId}`),

  reorderSections: (cartaId: number, sectionIds: number[]) =>
    api.put<{ message: string; carta: Carta }>(`/cartas/${cartaId}/sections/reorder`, { section_ids: sectionIds }),

  // Item management
  addItem: (cartaId: number, sectionId: number, data: {
    name: string;
    description?: string;
    price: number;
    recipe_id?: number;
    allergens?: string[];
    dietary_icons?: string[];
    image_url?: string;
    is_featured?: boolean;
    is_available?: boolean;
    variants?: CartaItemVariant[];
  }) => api.post<{ message: string; item: CartaItem }>(`/cartas/${cartaId}/sections/${sectionId}/items`, data),

  updateItem: (cartaId: number, itemId: number, data: Partial<{
    carta_section_id: number;
    recipe_id: number;
    name: string;
    description: string;
    price: number;
    allergens: string[];
    dietary_icons: string[];
    image_url: string;
    is_featured: boolean;
    is_available: boolean;
    variants: CartaItemVariant[];
  }>) => api.put<{ message: string; item: CartaItem }>(`/cartas/${cartaId}/items/${itemId}`, data),

  deleteItem: (cartaId: number, itemId: number) =>
    api.delete<{ message: string }>(`/cartas/${cartaId}/items/${itemId}`),

  reorderItems: (cartaId: number, sectionId: number, itemIds: number[]) =>
    api.put<{ message: string; section: CartaSection }>(`/cartas/${cartaId}/sections/${sectionId}/items/reorder`, { item_ids: itemIds }),

  importRecipe: (cartaId: number, recipeId: number, sectionId: number) =>
    api.post<{ message: string; item: CartaItem }>(`/cartas/${cartaId}/items/import-recipe`, { recipe_id: recipeId, section_id: sectionId }),

  translate: (cartaId: number, targetLanguages: string[]) =>
    api.post<{ message: string; carta: Carta; translated_languages: string[] }>(`/cartas/${cartaId}/translate`, { target_languages: targetLanguages }),
};

// Shifts API (Planificador de Turnos)
export interface ShiftData {
  id: number;
  user_id: number;
  user_name: string;
  date: string;
  shift_type: 'morning' | 'afternoon' | 'night' | 'split';
  start_time: string;
  end_time: string;
  break_minutes: number;
  hours: number;
  estimated_cost: number | null;
  notes: string | null;
}

export interface ShiftTemplateData {
  id: number;
  name: string;
  shifts: Array<{
    user_id: number;
    day_of_week: number;
    shift_type: string;
    start_time: string;
    end_time: string;
    break_minutes?: number;
  }>;
  creator?: { id: number; name: string };
}

export const shiftsApi = {
  list: (params: { restaurant_id: number; week: string }) =>
    api.get<{ shifts: ShiftData[]; week_start: string; week_end: string }>('/shifts', { params }),

  myShifts: (params: { restaurant_id: number; week?: string }) =>
    api.get<{ shifts: ShiftData[]; today: { shift_type: string; start_time: string; end_time: string } | null }>('/shifts/my', { params }),

  summary: (params: { restaurant_id: number; week: string }) =>
    api.get<{
      total_hours: number;
      total_cost: number;
      total_shifts: number;
      by_employee: Array<{ user_id: number; user_name: string; hours: number; cost: number; shifts_count: number }>;
      by_day: Array<{ date: string; day_name: string; hours: number; cost: number; shifts_count: number }>;
    }>('/shifts/summary', { params }),

  create: (data: { restaurant_id: number; user_id: number; date: string; shift_type: string; start_time: string; end_time: string; break_minutes?: number; notes?: string }) =>
    api.post<{ message: string; shift: ShiftData }>('/shifts', data),

  update: (id: number, data: Partial<{ shift_type: string; start_time: string; end_time: string; break_minutes: number; notes: string }>) =>
    api.put<{ message: string; shift: ShiftData }>(`/shifts/${id}`, data),

  delete: (id: number) =>
    api.delete<{ message: string }>(`/shifts/${id}`),

  bulkCopy: (data: { restaurant_id: number; shifts: Array<{ user_id: number; date: string; shift_type: string; start_time: string; end_time: string; break_minutes?: number; notes?: string }> }) =>
    api.post<{ message: string; created: number; skipped: number }>('/shifts/bulk-copy', data),

  templates: (params: { restaurant_id: number }) =>
    api.get<{ templates: ShiftTemplateData[] }>('/shift-templates', { params }),

  createTemplate: (data: { restaurant_id: number; name: string; shifts: ShiftTemplateData['shifts'] }) =>
    api.post<{ message: string; template: ShiftTemplateData }>('/shift-templates', data),

  applyTemplate: (id: number, data: { week: string }) =>
    api.post<{ message: string; created: number; skipped: number }>(`/shift-templates/${id}/apply`, data),

  deleteTemplate: (id: number) =>
    api.delete<{ message: string }>(`/shift-templates/${id}`),
};

// Public Carta API (no authentication)
export const publicCartaApi = {
  get: (hash: string, lang?: string) => api.get<{
    carta: {
      id: number;
      name: string;
      description: string | null;
      template: string;
      design_settings: CartaDesignSettings;
      default_language: string;
      available_languages: string[];
      current_language: string;
      restaurant: {
        name: string | null;
        address: string | null;
        phone: string | null;
      };
      sections: Array<{
        id: number;
        name: string;
        description: string | null;
        items: Array<{
          id: number;
          name: string;
          description: string | null;
          price: number;
          formatted_price: string;
          allergens: AllergenInfo[];
          dietary_icons: DietaryIconInfo[];
          image_url: string | null;
          is_featured: boolean;
          variants: CartaItemVariant[] | null;
        }>;
      }>;
      view_count: number;
    };
    allergens_legend: Record<string, AllergenInfo>;
    dietary_icons_legend: Record<string, DietaryIconInfo>;
    supported_languages: Record<string, string>;
  }>(`/c/${hash}`, { params: lang ? { lang } : undefined }),
};

// ─── Nutrition API (OpenFoodFacts, free) ────────────────────────────────────

export interface NutritionSearchResult {
  name: string;
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  fiber: number | null;
  sugar: number | null;
  saturated_fat: number | null;
  sodium: number | null;
  source: 'openfoodfacts';
}

export const nutritionApi = {
  search: (q: string) =>
    api.get<{ query: string; results: NutritionSearchResult[] }>('/nutrition/search', { params: { q } }),
};

// Goods Reception Types (Traceability)
export interface GoodsReception {
  id: number;
  restaurant_id: number;
  supplier_id: number | null;
  reception_date: string;
  delivery_note_number: string | null;
  status: 'pending_check' | 'accepted' | 'partial_rejection' | 'rejected';
  general_notes: string | null;
  received_by: number | null;
  items: GoodsReceptionItem[];
  supplier?: { id: number; name: string };
  items_count?: number;
  total_value?: number;
  created_at: string;
}

export interface GoodsReceptionItem {
  id: number;
  ingredient_id: number;
  lot_number: string | null;
  expiry_date: string | null;
  temperature_received: number | null;
  quality_status: 'ok' | 'observation' | 'rejected';
  quality_notes: string | null;
  quantity_received: number;
  unit: string;
  unit_cost: number | null;
  storage_location_id: number | null;
  ingredient?: { id: number; name: string; unit: string };
  storage_location?: { id: number; name: string; type: string };
}

export interface GoodsReceptionCreatePayload {
  restaurant_id: number;
  supplier_id?: number | null;
  reception_date?: string;
  delivery_note_number?: string;
  general_notes?: string;
  items: {
    ingredient_id: number;
    quantity_received: number;
    unit: string;
    unit_cost?: number | null;
    lot_number?: string | null;
    expiry_date?: string | null;
    temperature_received?: number | null;
    quality_status?: 'ok' | 'observation' | 'rejected';
    quality_notes?: string | null;
    storage_location_id?: number | null;
  }[];
}

// Goods Reception API
export const goodsReceptionsApi = {
  list: (params?: Record<string, any>) => api.get('/goods-receptions', { params }),
  create: (data: GoodsReceptionCreatePayload) => api.post('/goods-receptions', data),
  show: (id: number) => api.get(`/goods-receptions/${id}`),
};

// Temperature Logs
export interface TemperatureLog {
  id: number;
  storage_location_id: number;
  temperature: number;
  recorded_at: string;
  recorded_by: number | null;
  out_of_range: boolean;
  corrective_action: string | null;
  notes: string | null;
  storage_location?: { id: number; name: string; type: string; min_temperature: number | null; max_temperature: number | null };
  recorded_by_user?: { id: number; name: string };
}

export interface TemperatureTodayLocation {
  id: number;
  name: string;
  type: string;
  min_temperature: number | null;
  max_temperature: number | null;
  readings_today: TemperatureLog[];
  status: 'complete' | 'pending' | 'alert';
}

export interface TemperatureTodayResponse {
  locations: TemperatureTodayLocation[];
  summary: { total_locations: number; complete: number; pending: number; alerts: number };
}

export const temperatureApi = {
  today: (restaurantId: number) => api.get<TemperatureTodayResponse>('/temperature-logs/today', { params: { restaurant_id: restaurantId } }),
  list: (params?: Record<string, any>) => api.get('/temperature-logs', { params }),
  create: (data: { restaurant_id: number; storage_location_id: number; temperature: number; recorded_at?: string; notes?: string; corrective_action?: string }) => api.post('/temperature-logs', data),
};

// APPCC Tasks
export const appccTasksApi = {
  today: (params: { restaurant_id: number; date?: string }) => api.get('/appcc-tasks/today', { params }),
  list: (params: { restaurant_id: number }) => api.get('/appcc-tasks', { params }),
  seedTemplates: (restaurantId: number) => api.post('/appcc-tasks/seed-templates', { restaurant_id: restaurantId }),
  calendar: (params: { restaurant_id: number; month: string }) => api.get<{ month: string; calendar: Record<string, { total: number; completed: number; pending: number; compliance: number } | null> }>('/appcc-tasks/calendar', { params }),
};

// APPCC Records
export interface AppccRecord {
  id: number;
  restaurant_id: number;
  record_type: 'cleaning' | 'pest_control' | 'corrective_action' | 'incident' | 'training' | 'maintenance';
  title: string;
  description: string | null;
  scheduled_date: string | null;
  completed_date: string | null;
  status: 'pending' | 'completed' | 'overdue';
  responsible_user_id: number | null;
  evidence_photo: string | null;
  created_by: number | null;
  responsible_user?: { id: number; name: string } | null;
  created_at: string;
  updated_at: string;
}

export interface AppccSummary {
  total: number;
  completed: number;
  pending: number;
  overdue: number;
  compliance_score: number;
  by_type: Record<string, { total: number; completed: number; pending: number; overdue: number }>;
}

export const appccRecordsApi = {
  list: (params?: Record<string, any>) => api.get('/appcc-records', { params }),
  create: (data: { restaurant_id: number; record_type: string; title: string; description?: string; scheduled_date?: string; responsible_user_id?: number }) => api.post('/appcc-records', data),
  update: (id: number, data: Record<string, any>) => api.put(`/appcc-records/${id}`, data),
  summary: (params: { restaurant_id: number }) => api.get<AppccSummary>('/appcc-records/summary', { params }),
};

export const despiecesApi = {
  list: (params: { restaurant_id: number; search?: string; status?: string; page?: number; per_page?: number }) =>
    api.get<{ data: Despiece[]; meta: { current_page: number; last_page: number; per_page: number; total: number } }>('/despieces', { params }),

  get: (id: number) =>
    api.get<{ despiece: Despiece; recipe_usage: Array<{ recipe_id: number; recipe_name: string; ingredient_name: string }> }>(`/despieces/${id}`),

  create: (data: {
    restaurant_id: number;
    name: string;
    description?: string;
    ingredient_id: number;
    input_quantity: number;
    input_unit: string;
    allocation_method: 'weight' | 'value';
    notes?: string;
    status?: string;
    outputs: Array<{
      name: string;
      quantity: number;
      unit: string;
      value_per_unit?: number;
      is_waste?: boolean;
      sort_order?: number;
      notes?: string;
    }>;
  }) => api.post<{ message: string; despiece: Despiece }>('/despieces', data),

  update: (id: number, data: Record<string, unknown>) =>
    api.put<{ message: string; despiece: Despiece }>(`/despieces/${id}`, data),

  delete: (id: number) =>
    api.delete<{ message: string; affected_recipes?: Array<{ id: number; name: string }> }>(`/despieces/${id}`),

  duplicate: (id: number) =>
    api.post<{ message: string; despiece: Despiece }>(`/despieces/${id}/duplicate`),
};

// Traceability Reports
export interface TraceabilityForwardResult {
  ingredient: { id: number; name: string; unit: string };
  lot_number: string | null;
  receptions: Array<{
    id: number;
    date: string | null;
    supplier: string | null;
    delivery_note: string | null;
    quantity: number;
    unit: string;
    lot_number: string | null;
    expiry_date: string | null;
    temperature: number | null;
    quality_status: string | null;
  }>;
  usage: Array<{
    date: string;
    type: string;
    quantity: number;
    unit: string;
    reference: string | null;
    notes: string | null;
  }>;
  current_stock: number;
}

export interface TraceabilityBackwardResult {
  recipe: { id: number; name: string };
  production_date: string;
  ingredients_used: Array<{
    ingredient: { id: number; name: string; unit: string };
    quantity: number;
    lot_number: string | null;
    supplier: string | null;
    reception_date: string | null;
    expiry_date: string | null;
  }>;
}

export const traceabilityApi = {
  forward: (params: { ingredient_id: number; lot_number?: string }) =>
    api.get<TraceabilityForwardResult>('/traceability/forward', { params }),
  backward: (params: { recipe_id: number; date: string }) =>
    api.get<TraceabilityBackwardResult>('/traceability/backward', { params }),
};

// OCR Scan History
export interface OcrScanSummary {
  id: number;
  filename: string;
  status: 'completed' | 'failed' | 'processing';
  document_type: 'albaran' | 'factura';
  supplier: string | null;
  supplier_id: number | null;
  invoice_number: string | null;
  invoice_date: string | null;
  items_extracted: number;
  items_matched: number;
  total_amount: string | null;
  pages_count: number;
  has_image: boolean;
  error_message: string | null;
  created_at: string;
}

export interface OcrScanDetail extends OcrScanSummary {
  items: Array<{
    name: string;
    quantity: number;
    unit: string;
    unit_price: number;
    total_price: number;
  }>;
  subtotal: number | null;
  tax: number | null;
  total: number | null;
}

export const ocrHistoryApi = {
  list: (params: { restaurant_id: number; status?: string; document_type?: string; supplier?: string; from?: string; to?: string; page?: number; per_page?: number }) =>
    api.get<{ data: OcrScanSummary[]; current_page: number; last_page: number; per_page: number; total: number }>('/ocr-history', { params }),
  detail: (id: number, restaurantId: number) =>
    api.get<OcrScanDetail>(`/ocr-history/${id}`, { params: { restaurant_id: restaurantId } }),
  image: (id: number, restaurantId: number) =>
    api.get<Blob>(`/ocr-history/${id}/image`, { params: { restaurant_id: restaurantId }, responseType: 'blob' }),
  feedback: (id: number, data: { rating: 'positive' | 'negative'; text?: string }) =>
    api.post<{ message: string }>(`/ocr-history/${id}/feedback`, data),
};

// --- Reconciliation ---

export interface ReconciliationSummary {
  factura_total: number;
  albaranes_total: number;
  albaranes_count: number;
  difference: number;
  difference_pct: number;
  status: 'ok' | 'minor' | 'warning';
}

export interface ReconciliationDiscrepancy {
  item: string;
  type: 'solo_factura' | 'solo_albaran' | 'precio_diferente';
  factura_qty: number | null;
  factura_price: number | null;
  factura_total: number | null;
  albaran_qty: number | null;
  albaran_price: number | null;
  albaran_total: number | null;
  diff: number;
}

export interface ReconciliationResult {
  factura: OcrScanSummary & { items: Array<{ name: string; quantity: number; unit_price: number; total_price: number }>; subtotal: number | null; tax: number | null };
  albaranes: Array<OcrScanSummary & { items: Array<{ name: string; quantity: number; unit_price: number; total_price: number }> }>;
  summary: ReconciliationSummary;
  discrepancies: ReconciliationDiscrepancy[];
}

export const reconciliationApi = {
  facturas: (params: { restaurant_id: number }) =>
    api.get<{ data: OcrScanSummary[] }>('/reconciliation/facturas', { params }),
  albaranes: (params: { restaurant_id: number; supplier_id?: number; exclude_ids?: string }) =>
    api.get<{ data: OcrScanSummary[] }>('/reconciliation/albaranes', { params }),
  reconcile: (facturaId: number, manualIds?: number[]) =>
    api.get<ReconciliationResult>(`/reconciliation/${facturaId}`, { params: manualIds?.length ? { manual_ids: manualIds.join(',') } : {} }),
};

// --- P&L Simplificado ---

export interface PnlCategoryDetail {
  items: Array<{ id: number; name: string; amount: number }>;
  total: number;
}

export interface PnlRevenueBreakdown {
  food: number;
  drink: number;
  delivery: number;
  other: number;
  food_percent: number;
  drink_percent: number;
  delivery_percent: number;
  other_percent: number;
  source: 'manual' | 'backfill' | 'pos';
}

// La forma EXACTA que devuelve /api/pnl/{year}/{month} tras los planes
// 10-02, 10-04 y 10-05 (ver PnlService::calculate() en el backend). Este
// tipo llevaba desfasado desde la fase 9 (sin `variable_expenses` ni
// `capex`) y desde el plan 10-04/10-05 (sin `labor`, `prime_cost`,
// `revenue.breakdown`): un frontend que consume un payload que su tipo no
// describe es como una linea de la cascada se pierde sin que nadie lo note.
export interface PnlData {
  month: string;
  revenue: {
    total: number;
    source: string | null;
    notes: string | null;
    breakdown: PnlRevenueBreakdown | null;
    breakdown_source: 'manual' | 'backfill' | 'pos' | null;
  };
  material_cost: { total: number; percent: number };
  gross_margin: { total: number; percent: number };
  labor: { total: number; percent: number; source: 'manual' | 'expenses' | null };
  prime_cost: { total: number; percent: number };
  fixed_expenses: {
    by_category: Record<string, PnlCategoryDetail>;
    total: number;
    percent: number;
  };
  variable_expenses: {
    by_category: Record<string, PnlCategoryDetail>;
    total: number;
    percent: number;
  };
  capex: {
    by_category: Record<string, PnlCategoryDetail>;
    total: number;
    percent: number;
  };
  ebitda: { total: number; percent: number };
  previous_month: {
    month: string;
    revenue: number;
    material_cost: number;
    gross_margin: number;
    labor: number;
    prime_cost: number;
    fixed_expenses: number;
    variable_expenses: number;
    capex: number;
    ebitda: number;
    ebitda_percent: number;
  };
  has_revenue: boolean;
}

// 11 claves planas, igual que previous_month (la forma escalar de
// calculateBasic(), sin `source` ni `by_category`).
export interface PnlTrendPoint {
  month: string;
  revenue: number;
  material_cost: number;
  gross_margin: number;
  labor: number;
  prime_cost: number;
  fixed_expenses: number;
  variable_expenses: number;
  capex: number;
  ebitda: number;
  ebitda_percent: number;
}

export interface MonthlyLaborCost {
  id: number;
  restaurant_id: number;
  month: string;
  amount: number;
  notes: string | null;
}

export interface FixedExpense {
  id: number;
  restaurant_id: number;
  category: string;
  name: string;
  amount: number;
  is_recurring: boolean;
  effective_from: string;
  effective_until: string | null;
}

export const pnlApi = {
  show: (restaurantId: number, year: number, month: number) =>
    api.get<PnlData>(`/pnl/${year}/${month}`, { params: { restaurant_id: restaurantId } }),

  trend: (restaurantId: number, months?: number) =>
    api.get<{ data: PnlTrendPoint[] }>('/pnl/trend', { params: { restaurant_id: restaurantId, months } }),

  storeRevenue: (data: { restaurant_id: number; month: string; total_revenue: number; source?: string; notes?: string }) =>
    api.post<{ message: string; data: any }>('/pnl/revenue', data),

  listExpenses: (restaurantId: number) =>
    api.get<{ data: FixedExpense[] }>('/pnl/fixed-expenses', { params: { restaurant_id: restaurantId } }),

  storeExpense: (data: Partial<FixedExpense> & { restaurant_id: number; category: string; name: string; amount: number; effective_from: string }) =>
    api.post<{ message: string; data: FixedExpense }>('/pnl/fixed-expenses', data),

  deleteExpense: (id: number, restaurantId: number) =>
    api.delete<{ message: string }>(`/pnl/fixed-expenses/${id}`, { params: { restaurant_id: restaurantId } }),

  showLaborCost: (restaurantId: number, month: string) =>
    api.get<{ data: MonthlyLaborCost | null }>('/pnl/labor-cost', { params: { restaurant_id: restaurantId, month } }),

  storeLaborCost: (data: { restaurant_id: number; month: string; amount: number; notes?: string }) =>
    api.post<{ message: string; data: MonthlyLaborCost }>('/pnl/labor-cost', data),

  deleteLaborCost: (restaurantId: number, month: string) =>
    api.delete<{ message: string }>('/pnl/labor-cost', { params: { restaurant_id: restaurantId, month } }),

  storeRevenueBreakdown: (data: { restaurant_id: number; month: string; food: number; drink: number; delivery: number; other: number }) =>
    api.post<{ message: string; data: unknown }>('/pnl/revenue-breakdown', data),
};

export default api;
